/**
 * Cart validation system for Naked theme
 * Version 7
 * Validates:
 * - Minimum order value (10€)
 * - Cup quantities (must be multiple of 6)
 */
(function () {
  'use strict';

  // Configuration
  const config = {
    minOrderValue: 1000,  // 10€ in cents
    cupsMultiple: 6,
    checkDelay: 300,
    throttleTime: 200,
    endpoints: {
      cart: '/cart.js',
      bogos: '/apps/bogo'
    }
  };

  // Cart state
  const state = {
    isCheckingCart: false,
    lastCheckTimestamp: 0,
    isValidCart: true,
    validationErrors: [],
    cartInitialized: false,
    bogos: {
      lastCartTotal: 0,
      currentOffer: null
    }
  };

  // DOM Selectors
  const selectors = {
    mainCart: 'cart-items',
    cartDrawer: 'cart-drawer',
    cartNotification: 'cart-notification',
    checkoutButtons: 'button[name="checkout"], #checkout, #CartDrawer-Checkout',
    alertClass: 'cart-popup-alerts'
  };

  // Alert styles
  const alertStyles = `
    margin: 10px 0 20px 0;
    font-size: 14px;
    line-height: 1.4;
    color: #fff;
  `;

  // Cart Analysis Functions
  const cartAnalyzer = {
    analyzeCart(cart) {
      let totalCups = 0;
      let totalRegularPrice = 0;
      let hasCupProducts = false;

      cart.items.forEach(item => {
        const productType = item.product_type?.toLowerCase();
        if (productType === 'cup' || productType === 'cup box') {
          hasCupProducts = true;
          if (productType === 'cup') {
            totalCups += item.quantity;
          } else {
            totalCups += (item.quantity * 6);
          }
        }
        const regularPricePerItem = (item.compare_at_price > 0) ? item.compare_at_price : item.price;
        totalRegularPrice += regularPricePerItem * item.quantity;
      });

      return { totalCups, totalRegularPrice, hasCupProducts };
    },

    validateCart(totalCups, totalRegularPrice, hasCupProducts) {
      if (!hasCupProducts) {
        const meetsMinimumAmount = totalRegularPrice >= config.minOrderValue;
        return {
          isValid: meetsMinimumAmount,
          errors: meetsMinimumAmount ? [] : [`L'importo d'ordine minimo è di 10€ calcolato sul prezzo di listino.`]
        };
      }

      const meetsMinimumAmount = totalRegularPrice >= config.minOrderValue;
      const hasCupsInValidQuantity = totalCups % config.cupsMultiple === 0;

      const errors = [];
      if (!meetsMinimumAmount) {
        errors.push(`L'importo d'ordine minimo è di 10€ calcolato sul prezzo di listino.`);
      }
      if (!hasCupsInValidQuantity) {
        const remaining = Math.ceil(totalCups / config.cupsMultiple) * config.cupsMultiple - totalCups;
        errors.push(`Ricordati che puoi acquistare le cup in multipli di 6. Aggiungi ancora ${remaining} cup singole al carrello per procedere con l'ordine.`);
      }

      return {
        isValid: hasCupsInValidQuantity && meetsMinimumAmount,
        errors
      };
    },

    analyzeBogosMessage(cart) {
      // Check if BOGOS is loaded
      if (!window.FGSECOMAPP?.offers) return '';

      // Init bogosHelper if needed
      if (!window.bogosHelper) {
        const event = new Event('bogoshelper:ready');
        document.dispatchEvent(event);
        return '';
      }

      return window.bogosHelper.analyzeBogosOffer(cart.total_price / 100);
    }
  };

  // UI Update Functions
  const uiUpdater = {
    createAlertBox() {
      const alertBox = document.createElement('div');
      alertBox.className = selectors.alertClass;
      alertBox.style.cssText = alertStyles;
      return alertBox;
    },

    showCartAlert({ isValidCart, errorMessages, container }) {
      let alertBox = container.querySelector(`.${selectors.alertClass}`);
      if (!alertBox) {
        alertBox = this.createAlertBox();
        container.appendChild(alertBox);
      }

      if (!isValidCart && errorMessages.length > 0) {
        alertBox.innerHTML = errorMessages.join('<br><br>');
        alertBox.style.display = 'block';
      } else {
        alertBox.style.display = 'none';
        alertBox.innerHTML = '';
      }
    },

    updateMainCart(isValidCart, errorMessages) {
      const checkoutButton = document.querySelector('#checkout');
      if (checkoutButton) checkoutButton.disabled = !isValidCart;

      const contents = document.querySelector('.cart__footer .js-contents');
      if (contents) {
        this.showCartAlert({ isValidCart, errorMessages, container: contents });
      }
    },

    updateCartDrawer(isValidCart, errorMessages) {
      const cartNotification = document.querySelector(selectors.cartNotification);
      if (!cartNotification || cartNotification.offsetParent === null) return;

      const checkoutButton = cartNotification.querySelector('#CartDrawer-Checkout, [name="checkout"]');
      if (checkoutButton) checkoutButton.disabled = !isValidCart;

      let alertBox = cartNotification.querySelector(`.${selectors.alertClass}`);
      if (!alertBox) {
        alertBox = this.createAlertBox();
        const productBlock = cartNotification.querySelector('.cart-notification-product');
        if (productBlock?.parentNode) {
          productBlock.parentNode.insertBefore(alertBox, productBlock.nextSibling);
        } else {
          cartNotification.appendChild(alertBox);
        }
      }

      if (!isValidCart && errorMessages.length > 0) {
        alertBox.innerHTML = errorMessages.join('<br><br>');
        alertBox.style.display = 'block';
      } else {
        alertBox.style.display = 'none';
        alertBox.innerHTML = '';
      }
    },

    updateCartUI(isValidCart, errorMessages) {
      this.updateMainCart(isValidCart, errorMessages);
      this.updateCartDrawer(isValidCart, errorMessages);
    }
  };

  // Cart Validation Core Functions
  async function checkCartRequirements() {
    // Throttle checks
    const now = Date.now();
    if (state.isCheckingCart || (now - state.lastCheckTimestamp < config.throttleTime)) {
      return Promise.resolve(state.isValidCart);
    }

    state.isCheckingCart = true;
    state.lastCheckTimestamp = now;
    state.validationErrors = [];

    try {
      const response = await fetch(config.endpoints.cart);
      const cart = await response.json();

      if (cart.item_count === 0) {
        state.isValidCart = true;
        uiUpdater.updateCartUI(true, []);
        return true;
      }

      // BOGOS analysis first
      const bogosMessage = cartAnalyzer.analyzeBogosMessage(cart);
      if (bogosMessage) {
        state.validationErrors.push(bogosMessage);
      }

      // Then standard validation
      const { totalCups, totalRegularPrice, hasCupProducts } = cartAnalyzer.analyzeCart(cart);
      const { isValid, errors } = cartAnalyzer.validateCart(totalCups, totalRegularPrice, hasCupProducts);

      // Add other messages
      state.validationErrors.push(...errors);

      state.isValidCart = isValid;
      uiUpdater.updateCartUI(isValid, state.validationErrors);

      return isValid;
    } catch (error) {
      return true;
    } finally {
      state.isCheckingCart = false;
    }
  }

  // Cart Observers Setup
  function initializeCartObservers() {
    const mainCart = document.querySelector(selectors.mainCart);
    if (mainCart) {
      new MutationObserver(() => setTimeout(checkCartRequirements, config.checkDelay))
        .observe(mainCart, {
          childList: true,
          subtree: true,
          characterData: true
        });
    }

    const cartDrawer = document.querySelector(selectors.cartDrawer);
    if (cartDrawer) {
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (cartDrawer.hasAttribute('open')) {
              verifyCartInitialization();
            }
          } else if (mutation.type === 'childList') {
            setTimeout(checkCartRequirements, config.checkDelay);
          }
        }
      }).observe(cartDrawer, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    const cartNotification = document.querySelector(selectors.cartNotification);
    if (cartNotification) {
      new MutationObserver(() => setTimeout(checkCartRequirements, config.checkDelay))
        .observe(cartNotification, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
    }
  }

  // Verify cart initialization
  async function verifyCartInitialization() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      state.cartInitialized = cart.item_count > 0;

      if (state.cartInitialized) {
        await checkCartRequirements();
      } else {
        uiUpdater.updateCartUI(true, []);
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  // Validate cart before checkout
  function validateCartBeforeCheckout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    state.cartInitialized = true;

    checkCartRequirements().then(isValid => {
      if (isValid) {
        window.location.href = '/checkout';
      }
    });
  }

  // Init
  function initCartValidation() {
    const init = () => {
      verifyCartInitialization();
      initializeCartObservers();

      document.addEventListener('product:added', () => {
        state.cartInitialized = true;
        setTimeout(checkCartRequirements, config.checkDelay);
      });

      document.addEventListener('click', (event) => {
        if (event.target.matches(selectors.checkoutButtons) ||
          event.target.closest(selectors.checkoutButtons)) {
          validateCartBeforeCheckout(event);
        }
      }, true);
    };

    // Start base init
    init();

    // Watch for BOGOS ready
    const bogosObserver = new MutationObserver(() => {
      if (window.FGSECOMAPP?.offers) {
        bogosObserver.disconnect();
        checkCartRequirements();
      }
    });

    bogosObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Start validation on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartValidation);
  } else {
    initCartValidation();
  }
})();
